import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash2, ListChecks, CheckCircle2, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ws, fmtDate } from "./api";
import { useWorkspace } from "./WorkspaceContext";
import { PageHeader, Panel, Empty, Loading, Field, NativeSelect } from "./ui";

export const TaskDialog = ({ open, onOpenChange, kind = "task", onSaved }) => {
  const { isManagerUp, me } = useWorkspace();
  const [members, setMembers] = useState([]);
  const [f, setF] = useState({ title: "", description: "", due_at: "", end_at: "", priority: "normal", assigned_to: null });
  useEffect(() => { if (open && isManagerUp) ws("/crm/workspace/members").then((m) => setMembers(m.members)).catch(() => {}); }, [open, isManagerUp]);
  const save = async () => {
    const toIso = (v) => (v ? new Date(v).toISOString() : null);
    try {
      await ws("/crm/workspace/tasks", { method: "POST", body: { ...f, kind, due_at: toIso(f.due_at), end_at: toIso(f.end_at), assigned_to: f.assigned_to || me.crm_user_id } });
      toast.success(kind === "meeting" ? "Meeting scheduled" : "Task created");
      setF({ title: "", description: "", due_at: "", end_at: "", priority: "normal", assigned_to: null }); onOpenChange(false); onSaved();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} data-testid={`${kind}-dialog`}>
        <DialogHeader><DialogTitle>{kind === "meeting" ? "Schedule meeting" : "New task"}</DialogTitle></DialogHeader>
        <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid={`${kind}-title-input`} /></Field>
        <Field label="Details"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid={`${kind}-description-input`} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={kind === "meeting" ? "Starts" : "Due"}><Input type="datetime-local" value={f.due_at} onChange={(e) => setF({ ...f, due_at: e.target.value })} data-testid={`${kind}-due-input`} /></Field>
          {kind === "meeting"
            ? <Field label="Ends"><Input type="datetime-local" value={f.end_at} onChange={(e) => setF({ ...f, end_at: e.target.value })} data-testid="meeting-end-input" /></Field>
            : <Field label="Priority"><NativeSelect value={f.priority} onChange={(v) => setF({ ...f, priority: v || "normal" })} options={[{ value: "low", label: "Low" }, { value: "normal", label: "Normal" }, { value: "high", label: "High" }]} placeholder="Priority" testid="task-priority-select" /></Field>}
        </div>
        {isManagerUp && <Field label="Assign to"><NativeSelect value={f.assigned_to} onChange={(v) => setF({ ...f, assigned_to: v })} options={members.map((m) => ({ value: m.crm_user_id, label: m.name }))} placeholder="Me" testid={`${kind}-assignee-select`} /></Field>}
        <Button disabled={!f.title.trim() || (kind === "meeting" && !f.due_at)} onClick={save} className="bg-[#0B1530] hover:bg-[#1a2650]" data-testid={`${kind}-save-btn`}>Save</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function TasksPage() {
  const { me, isAdmin, isManagerUp } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const [tasks, setTasks] = useState(null);
  const [status, setStatus] = useState("open");
  const [open, setOpen] = useState(params.get("create") === "task");
  const [members, setMembers] = useState([]);
  const load = () => ws(`/crm/workspace/tasks?kind=task&status=${status}`).then((r) => setTasks(r.tasks)).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  useEffect(() => { if (params.get("create")) { setOpen(true); setParams({}, { replace: true }); } }, [params, setParams]);
  useEffect(() => { ws("/crm/workspace/members").then((m) => setMembers(m.members)).catch(() => {}); }, []);
  const who = (id) => (id === me.crm_user_id ? "Me" : members.find((m) => m.crm_user_id === id)?.name || "—");
  const toggle = async (t) => { try { await ws(`/crm/workspace/tasks/${t.task_id}`, { method: "PATCH", body: { status: t.status === "open" ? "done" : "open" } }); load(); } catch (e) { toast.error(e.message); } };
  const del = async (t) => { try { await ws(`/crm/workspace/tasks/${t.task_id}`, { method: "DELETE" }); toast.success("Task deleted"); load(); } catch (e) { toast.error(e.message); } };
  return (
    <div data-testid="tasks-page">
      <PageHeader title="Tasks" subtitle={isManagerUp ? "Your tasks and tasks for people you manage" : "Tasks assigned to you"}
        actions={<Button onClick={() => setOpen(true)} className="bg-[#0B1530] hover:bg-[#1a2650]" data-testid="new-task-btn"><Plus className="h-4 w-4 mr-1" />New task</Button>} />
      <div className="flex gap-2 mb-4">
        {["open", "done"].map((s) => <Button key={s} size="sm" variant={status === s ? "default" : "outline"} className={status === s ? "bg-[#0B1530]" : ""} onClick={() => setStatus(s)} data-testid={`tasks-filter-${s}`}>{s === "open" ? "Open" : "Completed"}</Button>)}
      </div>
      <Panel>
        {!tasks ? <Loading /> : tasks.length === 0 ? <Empty icon={ListChecks} title={status === "open" ? "No open tasks" : "No completed tasks"} /> : (
          <ul className="divide-y divide-slate-100 -my-2">
            {tasks.map((t) => (
              <li key={t.task_id} className="py-3 flex items-start gap-3" data-testid={`task-row-${t.task_id}`}>
                <button onClick={() => toggle(t)} className="mt-0.5" data-testid={`task-toggle-${t.task_id}`}>{t.status === "done" ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className="h-5 w-5 text-slate-300 hover:text-slate-500" />}</button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${t.status === "done" ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</p>
                  {t.description && <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>}
                  <p className="text-[11px] text-slate-400 mt-1">Due {fmtDate(t.due_at, true)} · {who(t.assigned_to)} · {t.priority}</p>
                </div>
                {(isAdmin || t.created_by === me.crm_user_id) && <button onClick={() => del(t)} className="text-slate-300 hover:text-red-600" data-testid={`task-delete-${t.task_id}`}><Trash2 className="h-4 w-4" /></button>}
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <TaskDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
